import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

/** GET/PUT `/structure/companies/{companyId}/shipping-methods`. */
export interface CompanyShippingMethods {
  companyId: string;
  shippingMethodIds: string[];
}

export interface CompanyShippingMethodsUpsertPayload {
  shippingMethodIds: string[];
}

/**
 * Formas de envío asociadas a una empresa (Operación / Configuración de procesos).
 *
 * Endpoints:
 *   - GET `/api/structure/companies/{companyId}/shipping-methods`
 *   - PUT `/api/structure/companies/{companyId}/shipping-methods`
 *     body: `{ shippingMethodIds: string[] }`
 */
@Injectable({ providedIn: 'root' })
export class CompanyShippingMethodsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  get(companyId: string): Observable<CompanyShippingMethods> {
    return this.httpClient
      .get<ApiResponse<CompanyShippingMethods>>(
        `${this.apiUrl}/${companyId}/shipping-methods`,
      )
      .pipe(
        map((response) => ({
          companyId:
            response.data?.companyId || companyId,
          shippingMethodIds: Array.isArray(response.data?.shippingMethodIds)
            ? response.data!.shippingMethodIds
            : [],
        })),
      );
  }

  update(
    companyId: string,
    payload: CompanyShippingMethodsUpsertPayload,
  ): Observable<CompanyShippingMethods> {
    return this.httpClient
      .put<ApiResponse<CompanyShippingMethods>>(
        `${this.apiUrl}/${companyId}/shipping-methods`,
        {
          shippingMethodIds: payload.shippingMethodIds ?? [],
        },
      )
      .pipe(
        map((response) => ({
          companyId: response.data?.companyId || companyId,
          shippingMethodIds: Array.isArray(response.data?.shippingMethodIds)
            ? response.data!.shippingMethodIds
            : payload.shippingMethodIds ?? [],
        })),
      );
  }
}
